import { createContext, useCallback, useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@shared/components/ui/alert-dialog'
import { logger } from '@shared/lib/utils/logger'

/**
 * Options for configuring a confirmation dialog
 */
export interface ConfirmOptions {
  title?: string
  description?: string
  confirmText?: string
  cancelText?: string
  variant?: 'default' | 'destructive'
}

/**
 * Value provided by ConfirmationContext
 */
export interface ConfirmationContextValue {
  confirm: (options: ConfirmOptions | string) => Promise<boolean>
}

/**
 * Internal state for managing the confirmation dialog
 */
interface ConfirmationState {
  isOpen: boolean
  options: Required<ConfirmOptions>
  resolve?: (value: boolean) => void
}

const DEFAULT_OPTIONS: Required<ConfirmOptions> = {
  title: 'Confirmar acción',
  description: '',
  confirmText: 'Confirmar',
  cancelText: 'Cancelar',
  variant: 'default',
}

export const ConfirmationContext = createContext<ConfirmationContextValue | undefined>(undefined)

/**
 * ConfirmationProvider - Global provider for confirmation dialogs
 *
 * Must be placed at the root of your application, typically in main.tsx
 *
 * @example
 * <ConfirmationProvider>
 *   <App />
 * </ConfirmationProvider>
 */
export function ConfirmationProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ConfirmationState>({
    isOpen: false,
    options: DEFAULT_OPTIONS,
  })

  const confirm = useCallback(
    (input: ConfirmOptions | string): Promise<boolean> => {
      return new Promise((resolve) => {
        // If a confirmation is already open, reject the new one
        if (state.isOpen) {
          logger.warn(
            'ConfirmationService: A confirmation dialog is already open. Rejecting new confirmation request.'
          )
          resolve(false)
          return
        }

        // Normalize input: string or object
        const options: Required<ConfirmOptions> = {
          ...DEFAULT_OPTIONS,
          ...(typeof input === 'string' ? { description: input } : input),
        }

        setState({
          isOpen: true,
          options,
          resolve,
        })
      })
    },
    [state.isOpen]
  )

  const handleConfirm = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isOpen: false,
    }))
    state.resolve?.(true)
  }, [state])

  const handleCancel = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isOpen: false,
    }))
    state.resolve?.(false)
  }, [state])

  return (
    <ConfirmationContext.Provider value={{ confirm }}>
      {children}
      <AlertDialog open={state.isOpen} onOpenChange={(open) => {
        if (!open) {
          handleCancel()
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{state.options.title}</AlertDialogTitle>
            {state.options.description && (
              <AlertDialogDescription>{state.options.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel}>
              {state.options.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className={
                state.options.variant === 'destructive'
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : ''
              }
            >
              {state.options.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmationContext.Provider>
  )
}
